/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, ember/require-tagless-components */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import EnrolmentStatus from 'linced-app/enums/enrolment-status';
import {
  filterValue,
  getNumericYearLevelFilter,
} from '@linc-technologies/linced-common/utils/filtering';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';
import { alias } from '@ember/object/computed';

export default class SearchResultsList extends Component {
  @service
  session;

  @service
  fieldOptions;

  @service
  strings;

  people = null;
  staffUsers = null;

  @alias('session.school.jurisdiction')
  jurisdiction;

  PersonType = PersonType;
  EnrolmentStatus = EnrolmentStatus;
  Jurisdiction = Jurisdiction;
  sortField = 'person.firstName';
  sortDir = 'asc';

  // Status / Person Type filters.
  @tracked
  current = true;

  @action
  setCurrent(value) {
    this.current = value;
  }

  @tracked
  preEnrolled = false;

  @action
  setPreEnrolled(value) {
    this.preEnrolled = value;
  }

  @tracked
  former = false;

  @action
  setFormer(value) {
    this.former = value;
  }

  @tracked
  staff = false;

  @action
  setStaff(value) {
    this.staff = value;
  }

  @tracked
  caregivers = false;

  @action
  setCaregivers(value) {
    this.caregivers = value;
  }

  // Field filters.
  @tracked
  lastNameFilter = '';

  @action
  setLastNameFilter(value) {
    this.lastNameFilter = value;
  }

  @tracked
  firstNameFilter = '';

  @action
  setFirstNameFilter(value) {
    this.firstNameFilter = value;
  }

  @tracked
  dobFilter = '';

  @action
  setDobFilter(value) {
    this.dobFilter = value;
  }

  @tracked
  jurisdictionalIdFilter = '';

  @action
  setJurisdictionalIdFilter(value) {
    this.jurisdictionalIdFilter = value;
  }

  @tracked
  genderFilter = '';

  @action
  setGenderFilter(value) {
    this.genderFilter = value;
  }

  @tracked
  yearLevelFilter = '';

  @action
  setYearLevelFilter(value) {
    this.yearLevelFilter = value;
  }

  @tracked
  homeroomFilter = '';

  @action
  setHomeroomFilter(value) {
    this.homeroomFilter = value;
  }

  @computed(
    'lastNameFilter',
    'firstNameFilter',
    'dobFilter',
    'jurisdictionalIdFilter',
    'genderFilter',
    'yearLevelFilter',
    'homeroomFilter',
  )
  get fieldFilters() {
    return [
      'lastName',
      'firstName',
      'dob',
      'jurisdictionalId',
      'gender',
      'yearLevel',
      'homeroom',
    ].reduce((filters, key) => {
      let value = this.get(`${key}Filter`);
      if (!isEmpty(value)) {
        filters.push({ key, value });
      }
      return filters;
    }, []);
  }

  @computed('session.activeSchool', 'fieldOptions.yearLevelOptions.[]')
  get yearLevelOptions() {
    let options = findBy(
      this.get('fieldOptions.yearLevelOptions'),
      'schoolId',
      this.get('session.activeSchool'),
    );
    if (isEmpty(options) || isEmpty(options.options)) {
      return [];
    }
    return options.options;
  }

  @computed(
    'people.[]',
    'current',
    'preEnrolled',
    'former',
    'staff',
    'caregivers',
    'fieldFilters.@each.{key,value}',
    'yearLevelOptions.[]',
  )
  get filteredResults() {
    let includedTypes = [];
    if (this.current || this.preEnrolled || this.former) {
      includedTypes.push(PersonType.LEARNER);
    }
    if (this.staff) {
      includedTypes.push(PersonType.STAFF);
    }
    if (this.caregivers) {
      includedTypes.push(PersonType.CAREGIVER);
    }

    let includedStatuses = [];
    if (this.current) {
      includedStatuses.push(EnrolmentStatus.CURRENT, EnrolmentStatus.PENDING);
    }
    if (this.preEnrolled) {
      includedStatuses.push(EnrolmentStatus.PRE_ENROLLED);
    }
    if (this.former) {
      includedStatuses.push(EnrolmentStatus.FORMER);
    }

    let yearLevelFilterValue = getNumericYearLevelFilter(
      findBy(this.fieldFilters, 'key', 'yearLevel'),
      this.yearLevelOptions,
    );

    return this.people.reduce((results, person) => {
      if (!includedTypes.includes(person.type)) {
        // Person type isn't selected.
        return results;
      }
      if (
        person.type === PersonType.LEARNER &&
        !includedStatuses.includes(person.enrolmentStatus)
      ) {
        // Enrolment status isn't selected.
        return results;
      }
      let status = PersonType.getLabel(person.type);
      switch (person.type) {
        case PersonType.LEARNER:
          status = EnrolmentStatus.getLabel(person.enrolmentStatus);
          break;
        case PersonType.STAFF:
          if (!isEmpty(person.userId)) {
            let user = this.staffUsers.find(
              (staffUser) => staffUser.id === person.userId,
            );
            if (isEmpty(user) || user.disabled) {
              status = `${status} (former)`;
            }
          } else {
            status = `${status} (former)`;
          }
      }

      let matchesFieldFilters =
        isEmpty(this.fieldFilters) ||
        this.fieldFilters.every((filter) => {
          let dataType = DataType.STRING;
          let key = filter.key;
          let value = filter.value;
          switch (filter.key) {
            case 'yearLevel':
              // Allow use of numeric filter operators.
              dataType = DataType.INTEGER;
              // Use numeric year level to handle select-based year levels.
              key = 'numericCurrentYearLevel';
              // Use pre-prepared filter value to handle select-based year levels.
              value = yearLevelFilterValue;
              break;

            case 'homeroom':
              key = 'homeroom.name';
              break;
          }
          return filterValue(person.get(key), dataType, value);
        });

      if (matchesFieldFilters) {
        results.push({
          person,
          status,
        });
      }

      return results;
    }, []);
  }
}
